/**
 * Melt & Pour calculator - weigh out a premade soap base with fragrance, colour and
 * additives, cost each bar, and save it as a normal recipe so batches, stock, sales
 * and labels work the same as for cold process.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '../contexts/AuthContext';
import useCurrencyTools from '../hooks/useCurrencyTools';
import { subscribeToUserCollection, addUserDoc, updateUserDoc } from '../utils/userDataHelper';
import { getCostPerUnit, getPricePerGram } from './utils/costCalculations';
import {
  MP_BASE_TYPES, DEFAULT_MELT_POUR, calculateMeltPour, meltPourCosts, toMeltPourRecipe, fromMeltPourRecipe,
} from './utils/meltPourCalculations';
import Breadcrumbs from '../Components/Breadcrumbs';
import Footer from '../Components/Footer';
import SafetyNotice from '../Components/SafetyNotice';
import '../styles/pages.css';
import './labcss/melt-pour.css';

const grams = (value) => `${(Math.round(value * 10) / 10).toLocaleString('en-IN', { maximumFractionDigits: 1 })} g`;
const categoryOf = (item) => (item.category || item.type || '').toLowerCase();
const costPerKgOf = (item) => {
  const perGram = getPricePerGram(getCostPerUnit(item), item.unit, item.densityGPerMl);
  return perGram > 0 ? Math.round(perGram * 1000 * 100) / 100 : '';
};
let extraKey = 0;

export default function MeltPourCalculator() {
  const { user } = useAuth();
  const { formatMoney, preferredCurrency } = useCurrencyTools();
  const navigate = useNavigate();
  const [form, setForm] = useState(DEFAULT_MELT_POUR);
  const [inventory, setInventory] = useState([]);
  const [recipeId, setRecipeId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', error: false, saved: false });

  useEffect(() => {
    if (!user) { setInventory([]); return undefined; }
    return subscribeToUserCollection('ingredients', setInventory);
  }, [user]);

  // Opened from My Recipes.
  useEffect(() => {
    const stored = sessionStorage.getItem('editMeltPour');
    if (!stored) return;
    try {
      const recipe = JSON.parse(stored);
      setForm(fromMeltPourRecipe(recipe));
      setRecipeId(recipe.id || null);
    } catch (error) {
      console.error('Unable to open melt-and-pour recipe:', error);
    } finally {
      sessionStorage.removeItem('editMeltPour');
    }
  }, []);

  const bases = useMemo(() => inventory.filter((i) => categoryOf(i).includes('base')), [inventory]);
  const fragrances = useMemo(() => inventory.filter((i) => /frag|essential/.test(categoryOf(i))), [inventory]);
  const additiveItems = useMemo(() => inventory.filter((i) => /additive|colou?r|clay|herb|scrub|liquid|functional/.test(categoryOf(i))), [inventory]);

  const result = useMemo(() => calculateMeltPour(form), [form]);
  const costs = useMemo(() => meltPourCosts(form, result), [form, result]);

  const set = (changes) => { setForm((f) => ({ ...f, ...changes })); setMessage({ text: '', error: false, saved: false }); };
  const setExtra = (key, changes) => set({ extras: form.extras.map((e) => (e.key === key ? { ...e, ...changes } : e)) });
  const addExtra = () => set({ extras: [...form.extras, { key: `new-${++extraKey}`, name: '', type: 'colour', mode: 'pct', value: 0.5, costPerKg: '', ingredientId: '' }] });
  const linkInventory = (id, list, fields) => {
    const item = list.find((i) => i.id === id);
    return item ? { [fields.id]: item.id, [fields.name]: item.name, [fields.cost]: costPerKgOf(item) } : { [fields.id]: '' };
  };

  const save = async () => {
    if (result.errors.length) return;
    setSaving(true);
    setMessage({ text: '', error: false, saved: false });
    try {
      const record = toMeltPourRecipe(form, result, costs, preferredCurrency);
      if (recipeId) await updateUserDoc('recipes', recipeId, record);
      else setRecipeId((await addUserDoc('recipes', record)).id);
      setMessage({ text: `“${record.name}” is saved in My Recipes.`, error: false, saved: true });
    } catch (error) {
      setMessage({ text: error.message || 'The recipe could not be saved.', error: true, saved: false });
    } finally {
      setSaving(false);
    }
  };

  const print = () => {
    document.body.classList.add('mp-printing');
    const done = () => { document.body.classList.remove('mp-printing'); window.removeEventListener('afterprint', done); };
    window.addEventListener('afterprint', done);
    window.print();
  };

  const baseLabel = form.baseName || `${form.baseType} base`;

  return (
    <div className="sl-page mp-page">
      <Helmet>
        <title>Melt and Pour Soap Calculator | SoapLab</title>
        <meta name="description" content="Work out how much melt-and-pour soap base, fragrance and colour you need for any number of bars, and what each bar costs." />
        <link rel="canonical" href="https://soaplab.in/melt-and-pour" />
      </Helmet>
      <div className="pageheader borderleft">
        <Breadcrumbs items={[{ label: 'Home', to: '/' }, { label: 'Recipes', to: '/recipes' }, { label: 'Melt & Pour' }]} />
        <div className="pageheader-row">
          <div className="pageheading">Melt &amp; Pour</div>
          <div className="pagedescription">Plan a batch from a premade soap base: how much base, fragrance and colour to weigh, and what each bar costs. Making soap from oils and lye? Use the <Link to="/calculator">lye calculator</Link>.</div>
        </div>
      </div>

      <div className="borderleft mp-layout">
        <div className="mp-inputs">
          <SafetyNotice variant="meltPour" />
          <section className="mp-card">
            <h2>1. Your batch</h2>
            <div className="mp-grid">
              <label className="mp-wide">Recipe name<input type="text" value={form.name} maxLength={120} placeholder="e.g. Rose goat milk gift bar" onChange={(e) => set({ name: e.target.value })} /></label>
              <label>Number of bars<input type="number" min="1" step="1" value={form.bars} onChange={(e) => set({ bars: e.target.value })} /></label>
              <label>Weight of one bar (g)<input type="number" min="1" step="any" value={form.barWeight} onChange={(e) => set({ barWeight: e.target.value })} /><small>Weigh a bar from your mould, or fill a cavity with water and weigh it.</small></label>
              <label>Extra for spills (%)<input type="number" min="0" max="25" step="any" value={form.overagePct} onChange={(e) => set({ overagePct: e.target.value })} /><small>Base left in the jug and on spatulas. 5% is a common allowance.</small></label>
              <label>Ready to sell after (days)<input type="number" min="0" max="30" step="1" value={form.readyDays} onChange={(e) => set({ readyDays: e.target.value })} /><small>Used for batch ready dates. No cure is needed.</small></label>
            </div>
          </section>

          <section className="mp-card">
            <h2>2. Soap base</h2>
            <div className="mp-grid">
              <label>Base type
                <select value={form.baseType} onChange={(e) => set({ baseType: e.target.value })}>
                  {MP_BASE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </label>
              {bases.length > 0 && (
                <label>From my inventory
                  <select value={form.baseIngredientId} onChange={(e) => set(linkInventory(e.target.value, bases, { id: 'baseIngredientId', name: 'baseName', cost: 'baseCostPerKg' }))}>
                    <option value="">Not linked</option>
                    {bases.map((b) => <option key={b.id} value={b.id}>{b.name} ({b.stock ?? 0} {b.unit || 'kg'} left)</option>)}
                  </select>
                </label>
              )}
              <label>Base name<input type="text" value={form.baseName} placeholder={`${form.baseType} base`} onChange={(e) => set({ baseName: e.target.value, baseIngredientId: '' })} /></label>
              <label>Cost per kg (optional)<input type="number" min="0" step="any" value={form.baseCostPerKg} onChange={(e) => set({ baseCostPerKg: e.target.value })} /></label>
            </div>
            {user && bases.length === 0 && <p className="mp-hint">Add your base to <Link to="/inventory">Inventory</Link> as a “Soap Base” so batches deduct it from your stock.</p>}
          </section>

          <section className="mp-card">
            <h2>3. Fragrance</h2>
            <div className="mp-grid">
              {fragrances.length > 0 && (
                <label>From my inventory
                  <select value={form.fragranceIngredientId} onChange={(e) => set(linkInventory(e.target.value, fragrances, { id: 'fragranceIngredientId', name: 'fragranceName', cost: 'fragranceCostPerKg' }))}>
                    <option value="">Not linked</option>
                    {fragrances.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                  </select>
                </label>
              )}
              <label>Fragrance name<input type="text" value={form.fragranceName} placeholder="e.g. Rose fragrance oil" onChange={(e) => set({ fragranceName: e.target.value, fragranceIngredientId: '' })} /></label>
              <label>Amount (% of base weight)<input type="number" min="0" max="10" step="any" value={form.fragrancePct} onChange={(e) => set({ fragrancePct: e.target.value })} /><small>Use your supplier’s maximum for melt-and-pour. Enter 0 for unscented.</small></label>
              <label>Cost per kg (optional)<input type="number" min="0" step="any" value={form.fragranceCostPerKg} onChange={(e) => set({ fragranceCostPerKg: e.target.value })} /></label>
            </div>
          </section>

          <section className="mp-card">
            <h2>4. Colours and additives</h2>
            {form.extras.length === 0 && <p className="mp-hint">Micas, clays, botanicals, exfoliants or anything else you add. Optional.</p>}
            {form.extras.map((extra, index) => (
              <div key={extra.key} className="mp-extra">
                {additiveItems.length > 0 && (
                  <label>Inventory
                    <select value={extra.ingredientId || ''} onChange={(e) => {
                      const item = additiveItems.find((i) => i.id === e.target.value);
                      setExtra(extra.key, item ? { ingredientId: item.id, name: item.name, costPerKg: costPerKgOf(item), type: /colou?r/.test(categoryOf(item)) ? 'colour' : 'additive' } : { ingredientId: '' });
                    }}>
                      <option value="">Not linked</option>
                      {additiveItems.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
                    </select>
                  </label>
                )}
                <label>Name<input type="text" value={extra.name} placeholder={`Additive ${index + 1}`} onChange={(e) => setExtra(extra.key, { name: e.target.value, ingredientId: '' })} /></label>
                <label>Amount
                  <span className="mp-amount">
                    <input type="number" min="0" step="any" value={extra.value} onChange={(e) => setExtra(extra.key, { value: e.target.value })} aria-label={`${extra.name || `Additive ${index + 1}`} amount`} />
                    <select value={extra.mode} onChange={(e) => setExtra(extra.key, { mode: e.target.value })} aria-label="Unit">
                      <option value="pct">% of base</option>
                      <option value="g">grams</option>
                    </select>
                  </span>
                </label>
                <label>Cost per kg<input type="number" min="0" step="any" value={extra.costPerKg || ''} onChange={(e) => setExtra(extra.key, { costPerKg: e.target.value })} /></label>
                <button type="button" className="mp-remove" aria-label={`Remove ${extra.name || `additive ${index + 1}`}`} onClick={() => set({ extras: form.extras.filter((e) => e.key !== extra.key) })}>×</button>
              </div>
            ))}
            <button type="button" className="sl-btn sl-btn-secondary" onClick={addExtra}>+ Add colour or additive</button>
          </section>

          <section className="mp-card">
            <h2>5. Costs and price (optional)</h2>
            <div className="mp-grid">
              <label>Packaging per bar<input type="number" min="0" step="any" value={form.packagingPerBar} onChange={(e) => set({ packagingPerBar: e.target.value })} /></label>
              <label>Labour per bar<input type="number" min="0" step="any" value={form.laborPerBar} onChange={(e) => set({ laborPerBar: e.target.value })} /></label>
              <label>Markup (%)<input type="number" min="0" step="any" value={form.markupPercent} onChange={(e) => set({ markupPercent: e.target.value })} /><small>100% markup sells at twice the cost.</small></label>
            </div>
            <label className="mp-wide mp-notes">Notes<textarea rows={2} value={form.notes} maxLength={2000} onChange={(e) => set({ notes: e.target.value })} placeholder="Mould, design, supplier batch, anything you want to remember" /></label>
          </section>
        </div>

        <aside className="mp-results mp-print" aria-live="polite">
          <h2>{form.name || 'Melt & pour batch'}</h2>
          <p className="mp-sub">{result.bars} bars × {grams(result.barWeight)}{Number(form.overagePct) > 0 ? ` + ${form.overagePct}% extra` : ''}</p>

          {result.errors.length > 0 ? (
            <ul className="mp-errors" role="alert">{result.errors.map((e) => <li key={e}>{e}</li>)}</ul>
          ) : (
            <>
              <table className="mp-table">
                <thead><tr><th>Weigh out</th><th>Amount</th></tr></thead>
                <tbody>
                  <tr><td>{baseLabel}</td><td>{grams(result.baseGrams)}</td></tr>
                  {result.fragranceGrams > 0 && <tr><td>{form.fragranceName || 'Fragrance'} <small>({form.fragrancePct}% of base)</small></td><td>{grams(result.fragranceGrams)}</td></tr>}
                  {result.extraLines.filter((e) => e.grams > 0).map((e) => (
                    <tr key={e.key}><td>{e.name || 'Additive'} {e.mode === 'pct' && <small>({e.value}% of base)</small>}</td><td>{grams(e.grams)}</td></tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr><th>Total to pour</th><th>{grams(result.totalGrams)}</th></tr>
                </tfoot>
              </table>

              {result.warnings.length > 0 && <ul className="mp-warnings">{result.warnings.map((w) => <li key={w}>{w}</li>)}</ul>}

              <div className="mp-costs">
                <h3>Cost</h3>
                <dl>
                  <div><dt>Ingredients</dt><dd>{formatMoney(costs.material)}</dd></div>
                  {costs.packaging > 0 && <div><dt>Packaging</dt><dd>{formatMoney(costs.packaging)}</dd></div>}
                  {costs.working > 0 && <div><dt>Labour</dt><dd>{formatMoney(costs.working)}</dd></div>}
                  <div className="mp-strong"><dt>Cost per bar</dt><dd>{formatMoney(costs.perBar)}</dd></div>
                  <div className="mp-strong"><dt>Suggested price ({costs.markup}% markup)</dt><dd>{formatMoney(costs.sellingPrice)}</dd></div>
                </dl>
                {costs.unpriced.length > 0 && <p className="mp-hint">No cost entered for {costs.unpriced.join(', ')}, so the cost per bar is too low.</p>}
              </div>

              <div className="mp-steps">
                <h3>Making it</h3>
                <ol>
                  <li>Cut the base into small cubes and melt it gently in short bursts or a double boiler. Do not let it boil. Follow your supplier’s melting and pouring temperatures.</li>
                  <li>Stir in colour first, then fragrance, without whisking air in.</li>
                  <li>Pour, then spritz the surface with rubbing alcohol (isopropyl, 90%+) to pop bubbles.</li>
                  <li>Unmould once fully hard and wrap promptly in cling film or shrink wrap so the bars don’t sweat.</li>
                </ol>
              </div>
            </>
          )}

          <div className="mp-actions">
            {user ? (
              <button type="button" className="sl-btn sl-btn-primary" onClick={save} disabled={saving || result.errors.length > 0}>
                {saving ? 'Saving…' : recipeId ? 'Update recipe' : 'Save recipe'}
              </button>
            ) : (
              <Link className="sl-btn sl-btn-primary" to="/login">Sign in to save</Link>
            )}
            <button type="button" className="sl-btn sl-btn-secondary" onClick={print} disabled={result.errors.length > 0}>Print sheet</button>
            {message.saved && <button type="button" className="sl-btn sl-btn-secondary" onClick={() => navigate('/batches')}>Make a batch →</button>}
          </div>
          {message.text && <p className={message.error ? 'mp-message error' : 'mp-message'} role="status">{message.text}</p>}
        </aside>
      </div>
      <Footer />
    </div>
  );
}
